// @ts-check
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import templates from '@bottlebooks/gatsby-plugin-california-pdf/src/templates';
import {
  Button,
  CloseIcon,
  DownloadIcon,
  Link,
  Stack,
  Text,
  Title,
} from '@bottlebooks/gatsby-theme-base';
import { Trans } from '@lingui/macro';
import Dialog from '@reach/dialog';
import { PDFDownloadLink } from '@react-pdf/renderer';
import React from 'react';
import { jsx } from 'theme-ui';
import { useFavorites } from '..';
import useSiteConfig from '@bottlebooks/gatsby-theme-event/src/components/useSiteConfig';
import useLocale from '@bottlebooks/gatsby-theme-event/src/useLocale';
import { useCollection } from '@bottlebooks/gatsby-theme-event/src/components/SiteSearch/useEventData';
import useUser from '../../../gatsby-plugin-firebase-auth/src/useUser';
import useTastingNoteExport from '../../../gatsby-plugin-tastingnotes/src/useTastingNoteExport';
import useMyListQuery from './useMyListQuery';

/**
 * @param {{ onClose: () => void;}} props
 */
export default function CatalogExportDialog({ onClose }) {
  return (
    <Dialog
      onDismiss={onClose}
      aria-label="Catalog export dialog"
      sx={{
        position: 'relative',
        width: ['100vw', 600],
        marginTop: [0, '10vh'],
        height: ['100vh', 'auto'],
      }}
    >
      <Button
        variant="text"
        onClick={onClose}
        sx={{ position: 'absolute', top: 2, right: 2 }}
      >
        <CloseIcon size="xsmall" />
      </Button>
      <Title variant="small">
        <Trans>Download your personal tasting booklet</Trans>
      </Title>
      <Text sx={{ marginBottom: 3 }}>
        <Trans>
          Your personal tasting booklet contains all the wines you have
          bookmarked, rated, or written tasting notes for.
        </Trans>
      </Text>
      <CatalogExport />
    </Dialog>
  );
}

function CatalogExport() {
  const { isLoading: isLoadingFavorites, favorites } = useFavorites();
  const { exportAsExcel: exportTastingNotes } = useTastingNoteExport();
  if (isLoadingFavorites) return null;
  if (favorites?.length === 0)
    return (
      <Text sx={{ color: 'brand.warningRed' }}>
        <Trans>
          You have not added any exhibitors or products to your list. After you
          add items to your list, come back and your personal tasting booklet
          will be ready to download.
        </Trans>
      </Text>
    );

  return (
    <Stack justify="flex-end" sx={{ marginRight: [3, 0] }}>
      <DownloadMyTastingBooklet />
      <Text>
        <Link onClick={exportTastingNotes}>
          <DownloadIcon sx={{ marginRight: 2 }} />
          <Trans>Download as Excel</Trans>
        </Link>
      </Text>
    </Stack>
  );
}

export function DownloadMyTastingBooklet({ fileName = 'my-list.pdf' }) {
  const [user] = useUser();
  const { myListTemplate } = useSiteConfig();
  const locale = useLocale();
  const { collectionId } = useCollection();
  const template = templates[myListTemplate];
  const { loading, error, data } = useMyListQuery({
    query: template?.query,
    collectionId,
    locale,
  });
  if (loading) {
    return (
      <Text sx={{ color: 'primary' }}>
        <DownloadIcon sx={{ marginRight: 2 }} /> <Trans>Download as PDF</Trans>{' '}
        – <Trans>Loading data…</Trans>
      </Text>
    );
  }
  if (error) {
    console.error(error);
    return (
      <Text>
        <Trans>There was an error loading your list data.</Trans>
      </Text>
    );
  }
  const Document = template.layout;
  return (
    <PDFDownloadLink
      sx={{ variant: 'link.text' }}
      document={
        <Document
          data={data}
          collectionId={collectionId}
          locale={locale}
          user={user}
        />
      }
      fileName={fileName}
    >
      {({ blob, error }) => {
        if (error) {
          return (
            <Text>
              <Trans>There was an error creating your file.</Trans>
            </Text>
          );
        }
        if (!blob) {
          return (
            <Text sx={{ color: 'primary' }}>
              <DownloadIcon sx={{ marginRight: 2 }} />{' '}
              <Trans>Download as PDF</Trans> –{' '}
              <Trans>Creating your list…</Trans>
            </Text>
          );
        }
        return (
          <Text sx={{ variant: 'link.text', color: 'primary' }}>
            <DownloadIcon sx={{ marginRight: 2 }} />{' '}
            <Trans>Download as PDF</Trans>
          </Text>
        );
      }}
    </PDFDownloadLink>
  );
}
